import { getCharts } from '@/api/book';
import ChartTooltip from '@/components/bookStatus/ChartTooltip';
import { Icon } from '@successmode-ktp/kui';
import { add, format, sub } from 'date-fns';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';

type Props = {
  nations: string[];
  endDate: string;
  setEndDate: (date: string) => void;
};

function DailyChart({ endDate, setEndDate, nations }: Props) {
  const [first, second, third] = nations;

  const [chartData, setChartData] = useState<
    {
      name: string;
      total: number;
      first: number;
      second: number;
      third: number;
      etc: number;
    }[]
  >([]);
  const [domain, setDomain] = useState([0, 100]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeValue, setActiveValue] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({
    x: -9999,
    y: -9999,
  });

  const { data } = useQuery(
    ['chartsData', nations, endDate],
    () =>
      getCharts({
        nations,
        interval: 'DAILY',
        endDate,
      }),
    {
      onSuccess: (data) => {
        const newData = Object.entries(data.details).map(([date, counts]) => {
          return {
            name: format(new Date(date), 'yyyy-MM-dd'),
            total:
              (counts[first] || 0) +
              (counts[second] || 0) +
              (counts[third] || 0) +
              (counts['ETC'] || 0),
            first: counts[first] || 0,
            second: counts[second] || 0,
            third: counts[third] || 0,
            etc: counts['ETC'] || 0,
          };
        });
        setChartData(newData);

        const maxDataValue = Math.max(...newData.map((item) => item.total));
        const roundedMax = Math.ceil(maxDataValue / 10) * 10;
        setDomain([0, roundedMax || 100]);
      },
      keepPreviousData: true,
    },
  );

  const isPastEndDate = endDate !== format(new Date(), 'yyyy-MM-dd');

  const handleBarClick = (e: any, index: number) => {
    setActiveIndex(activeValue === e.name ? null : index);
    setActiveValue((prev) => (prev === e.name ? null : e.name));
    onChangeTooltipPos({
      tooltipX: e.tooltipPosition.x,
      tooltipY: e.tooltipPosition.y,
    });
  };

  const onChangeTooltipPos = ({
    tooltipX,
    tooltipY,
  }: {
    tooltipX: number;
    tooltipY: number;
  }) => {
    const y = tooltipY > 200 ? tooltipY - 150 : tooltipY;
    setTooltipPos({
      x: tooltipX - 50,
      y,
    });
  };

  useEffect(
    function resetActiveIndex() {
      const LAST_INDEX = 30;
      if (endDate === format(new Date(), 'yyyy-MM-dd')) {
        setActiveIndex(LAST_INDEX);
        setActiveValue(chartData[LAST_INDEX]?.name);
      }
    },
    [endDate, chartData],
  );

  return (
    <Wrapper>
      <PageButton
        onClick={() =>
          setEndDate(
            format(sub(new Date(endDate), { months: 1 }), 'yyyy-MM-dd'),
          )
        }
      >
        <Icon.ChevronLeft size={18} fillColor='#949494' />
      </PageButton>
      <ResponsiveContainer width='100%' height={320}>
        <ComposedChart
          data={chartData}
          margin={{
            top: 20,
            right: 20,
            bottom: 20,
            left: 0,
          }}
          onMouseLeave={() => setActiveIndex(null)}
        >
          <CartesianGrid stroke='#CBCCCE' vertical={false} />
          <XAxis
            dataKey='name'
            tickLine={false}
            axisLine={false}
            dy={10}
            tickCount={31}
            // interval={Math.floor(chartData.length / 6)}
            tick={({ x, y, payload }) => {
              const isActive = payload.value === activeValue;
              const today = format(new Date(), 'yyyy-MM-dd');
              return (
                <g>
                  <text
                    x={x}
                    y={y + 20}
                    textAnchor='middle'
                    fill={isActive ? '#0A84FF' : '#80848A'}
                    style={{
                      fontSize: '14px',
                      fontWeight: isActive ? 'bold' : 'normal',
                    }}
                  >
                    {format(new Date(payload.value), 'MM/dd')}
                  </text>
                  {payload.value === today && (
                    <text
                      x={x}
                      y={y + 34}
                      textAnchor='middle'
                      fill={isActive ? '#0A84FF' : '#80848A'}
                      style={{
                        fontSize: '12px',
                        fontWeight: 'normal',
                      }}
                    >
                      오늘
                    </text>
                  )}
                </g>
              );
            }}
          />
          <YAxis
            domain={domain}
            tickLine={false}
            axisLine={false}
            tickCount={3}
            tick={({ x, y, payload }) => {
              return (
                <text
                  x={x - 14}
                  y={y + 4}
                  textAnchor='right'
                  fill='#80848A'
                  style={{
                    fontSize: '12px',
                  }}
                >
                  {payload.value === 0 ? '0명' : payload.value}
                </text>
              );
            }}
          />
          <Bar
            dataKey='total'
            barSize={20}
            fill='#D3E2FD'
            radius={[2, 2, 0, 0]}
            onClick={(e, index) => handleBarClick(e, index)}
          >
            {chartData.map((entry, index) => (
              <Cell
                cursor='pointer'
                fill={index === activeIndex ? '#5089F8' : '#D3E2FD'}
                key={`cell-${index}`}
              />
            ))}
          </Bar>
          <Line
            type='monotone'
            activeDot={false}
            dot={false}
            dataKey='first'
            stroke='#24C8FF'
            isAnimationActive={false}
          />
          <Line
            type='monotone'
            activeDot={false}
            dot={false}
            dataKey='second'
            stroke='#74CE30'
            isAnimationActive={false}
          />
          <Line
            type='monotone'
            activeDot={false}
            dot={false}
            dataKey='third'
            stroke='#FF8ED0'
            isAnimationActive={false}
          />
          <Line
            type='monotone'
            activeDot={false}
            dot={false}
            dataKey='etc'
            stroke='#AF54FF'
            isAnimationActive={false}
          />

          {/* 커스텀 툴팁 */}
          {activeValue !== null &&
            chartData.find((d) => d.name === activeValue) && (
              <foreignObject
                x={tooltipPos?.x}
                y={tooltipPos?.y}
                width={132}
                height={120}
              >
                <div {...{ xmlns: 'http://www.w3.org/1999/xhtml' }}>
                  <ChartTooltip
                    hasTotal
                    nations={nations}
                    label={format(new Date(activeValue), 'M/d')}
                    data={chartData.find((d) => d.name === activeValue)}
                  />
                </div>
              </foreignObject>
            )}
        </ComposedChart>
      </ResponsiveContainer>
      <PageButton
        onClick={() =>
          setEndDate(
            format(add(new Date(endDate), { months: 1 }), 'yyyy-MM-dd'),
          )
        }
        disabled={!isPastEndDate}
      >
        <Icon.ChevronRight size={18} fillColor='#949494' />
      </PageButton>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbccce;
  border-radius: 100px;
  &:disabled {
    opacity: 0.25;
  }
`;

export default DailyChart;
